// ─── CreateProjectModal ───────────────────────────────────────────────────────
// Compact "Create Project" popup matching the XD reference:
//
//   Create Project
//   ────────────────────────────────────────
//   [Project Name]
//   ┌───────────────────────────────────────┐
//   │  Project Logo (232 x 70 pixels)       │
//   │  Drag and Drop Files or Browse        │
//   └───────────────────────────────────────┘
//   [License Key (Leave blank for Basic (free) Tier)]
//   [        ASSIGN LICENSE (outlined)     ]
//   Project Tier: Professional     ← green
//   ────────────────────────────────────────
//   [        CREATE PROJECT               ]
//                   Cancel
//
// The License Key row talks to /api/project/license/lookup/ to resolve
// a typed key into a tier name. Empty key → "Basic" (free). Invalid /
// expired key → the input turns red with the server's message.
//
// The Description and Overview fields the old modal collected are no
// longer part of the design; the underlying createProject API still
// accepts them, so if you re-introduce them later just pass extra
// values via `formData` — nothing else needs to change.

import { useRef, useState } from 'react'
import { X, AlertCircle, Loader, Check } from 'lucide-react'
import { projectsApi } from '../lib/projectsApi'
import { scanInput } from '../lib/inputSecurity'

export function CreateProjectModal({ isOpen, onClose, onSuccess }) {
  const [name, setName]                 = useState('')
  const [image, setImage]               = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [licenseKey, setLicenseKey]     = useState('')
  const [tier, setTier]                 = useState({ name: 'Basic', is_free: true })
  const [licenseError, setLicenseError] = useState(null)
  const [assigning, setAssigning]       = useState(false)
  const [isLoading, setIsLoading]       = useState(false)
  const [error, setError]               = useState(null)
  const fileInputRef = useRef(null)

  const reset = () => {
    setName(''); setImage(null); setImagePreview(null)
    setLicenseKey(''); setTier({ name: 'Basic', is_free: true }); setLicenseError(null)
    setError(null); setAssigning(false); setIsLoading(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImage(file)
    const reader = new FileReader()
    reader.onload = (ev) => setImagePreview(ev.target.result)
    reader.readAsDataURL(file)
    setError(null)
  }
  const handleDrop = (e) => {
    e.preventDefault()
    const file = e.dataTransfer?.files?.[0]
    if (!file) return
    setImage(file)
    const reader = new FileReader()
    reader.onload = (ev) => setImagePreview(ev.target.result)
    reader.readAsDataURL(file)
  }
  const handleRemoveImage = () => {
    setImage(null); setImagePreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Legacy Symfony rule: a valid license key is a 36-char UUID. We do
  // the length check client-side too so an obviously wrong string
  // fails fast without a round-trip.
  const licenseKeyIsCandidate = (licenseKey || '').trim().length > 0
  const licenseKeyLooksValid  = (licenseKey || '').trim().length === 36

  const handleAssignLicense = async () => {
    const key = (licenseKey || '').trim()
    setLicenseError(null)
    // Client-side validation: block the call when the input is empty
    // or the wrong length. Matches the Symfony backend's "Invalid
    // License" response, but avoids the round-trip.
    if (!key) {
      setLicenseError('Enter a license key first.')
      setTier({ name: 'Basic', is_free: true })
      return
    }
    if (key.length !== 36) {
      setLicenseError('Invalid License') // same wording as the legacy PHP
      setTier({ name: 'Basic', is_free: true })
      return
    }
    setAssigning(true)
    try {
      const resp = await projectsApi.lookupLicense(key)
      if (resp?.valid) {
        setTier(resp.tier || { name: 'Basic', is_free: true })
      } else {
        setTier({ name: 'Basic', is_free: true })
        setLicenseError(resp?.message || 'Invalid License')
      }
    } catch (err) {
      setLicenseError(err?.response?.data?.message || err?.message || 'License lookup failed')
    } finally {
      setAssigning(false)
    }
  }

  const handleSubmit = async (e) => {
    e?.preventDefault?.()
    setError(null)
    const trimmed = name.trim()
    if (!trimmed) { setError('Project name is required'); return }
    const secErr = scanInput(trimmed)
    if (secErr) { setError(secErr); return }

    setIsLoading(true)
    try {
      const result = await projectsApi.createProject(
        trimmed,
        '',                       // description — dropped in this design
        '',                       // overview   — dropped in this design
        image,
        licenseKey.trim(),
      )
      if (result.success) {
        onSuccess?.(result.data)
        reset()
        onClose()
      } else {
        setError(result.error || 'Failed to create project')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create project')
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => !isLoading && onClose()}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-800">Create Project</h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded flex items-center gap-2 text-sm text-red-700">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {/* Project Name */}
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Project Name"
            disabled={isLoading}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]"
          />

          {/* Logo drop-zone */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="border border-gray-300 rounded p-4 min-h-[110px] flex flex-col"
          >
            <p className="text-xs text-gray-500 mb-1">Project Logo (232 x 70 pixels)</p>
            {imagePreview ? (
              <div className="relative flex-1 flex items-center justify-center">
                <img src={imagePreview} alt="Preview" className="max-h-20 object-contain" />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                  aria-label="Remove image"
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-sm text-gray-700">
                Drag and Drop Files or&nbsp;
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading}
                  className="text-[#358ECD] hover:underline"
                >
                  Browse
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              disabled={isLoading}
              className="hidden"
            />
          </div>

          {/* License Key */}
          <input
            type="text"
            value={licenseKey}
            onChange={(e) => { setLicenseKey(e.target.value); setLicenseError(null) }}
            placeholder="License Key (Leave blank for Basic (free) Tier)"
            disabled={isLoading}
            className={`w-full px-3 py-2 border rounded text-sm focus:outline-none ${
              licenseError ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-[#358ECD]'
            }`}
          />
          <button
            type="button"
            onClick={handleAssignLicense}
            // Disabled while no candidate key is typed. Once anything is
            // typed the button is enabled — clicking with a wrong-length
            // key still fires the client-side "Invalid License" branch
            // so users get feedback immediately.
            disabled={isLoading || assigning || !licenseKeyIsCandidate}
            title={!licenseKeyIsCandidate ? 'Enter a license key first' : (licenseKeyLooksValid ? '' : 'License keys are 36 characters (UUID).')}
            className="w-full py-2 border border-[#358ECD] text-[#358ECD] text-sm font-bold tracking-wider rounded hover:bg-[#EBF5FB] transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent inline-flex items-center justify-center gap-2"
          >
            {assigning && <Loader size={14} className="animate-spin" />}
            ASSIGN LICENSE
          </button>
          {licenseError && (
            <p className="text-xs text-red-600 -mt-2 flex items-center gap-1">
              <AlertCircle size={12} /> {licenseError}
            </p>
          )}
          <p className="text-sm font-semibold text-gray-800 -mt-1">
            Project Tier:{' '}
            <span className={tier?.is_free ? 'text-gray-500' : 'text-green-600'}>
              {tier?.name || 'Basic'}
            </span>
            {!tier?.is_free && !licenseError && (
              <Check className="inline-block ml-1 text-green-600" size={14} />
            )}
          </p>

          {/* Actions */}
          <div className="pt-2 border-t border-gray-100">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 bg-[#358ECD] hover:bg-[#2A78B0] text-white text-sm font-bold tracking-wider rounded disabled:opacity-50 inline-flex items-center justify-center gap-2 mt-3"
            >
              {isLoading && <Loader size={14} className="animate-spin" />}
              CREATE PROJECT
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="w-full text-center text-sm text-[#358ECD] hover:underline mt-2 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
